// client/src/components/ofertas/OfertaImages.js
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useMutateOfertas } from '../../hooks/useOfertas';
import useAlert from '../../hooks/useAlert';
import { UploadCloud, Trash2, Loader2, Search as SearchIcon, XCircle, Star, Image as ImageIcon, Info } from 'lucide-react';
import GaleriaBuscaModal from '../galeria/GaleriaBuscaModal';
import GerarImagemIABox from '../ai/GerarImagemIABox';

const OfertaImages = ({ oferta, onUpdate, isCreating = false }) => {
    const { setAlert } = useAlert();

    const {
        updateOferta,
        uploadOfertaImagem,
        deleteOfertaImagem,
        setOfertaImagemPrincipal,
        isPending: loading
    } = useMutateOfertas();

    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [showGalleryModal, setShowGalleryModal] = useState(false);

    const imagensCustom = (oferta.imagens || []).map(img => ({ ...img, tipo: 'custom' }));
    const imagensGaleria = (oferta.imagensDaGaleria || []).map(img => ({ ...img, tipo: 'galeria' }));
    const todasImagens = [...imagensGaleria, ...imagensCustom];

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setImageFile(file);
            setImagePreview(URL.createObjectURL(file));
        }
    };

    const handleImageUpload = () => {
        if (!imageFile) return setAlert('Nenhum arquivo selecionado.', 'warning');
        
        uploadOfertaImagem.mutate({ ofertaId: oferta._id, file: imageFile }, {
            onSuccess: (response) => {
                setImageFile(null);
                setImagePreview(null);
                const input = document.getElementById('oferta-image-upload-component');
                if (input) input.value = '';
                onUpdate(response.data.data);
            },
            onError: (err) => {
                console.error(err);
            }
        });
    };

    const handleCustomImageDelete = (imageId) => {
        if (window.confirm('Tem certeza que deseja remover esta imagem customizada?')) {
            deleteOfertaImagem.mutate({ ofertaId: oferta._id, imageId: imageId }, {
                 onSuccess: (response) => {
                    onUpdate(response.data.data);
                 }
            });
        }
    };

    const handleGalleryImageSelect = (imagemSelecionada) => {
        const isAlreadyLinked = (oferta.imagensDaGaleria || []).some(img => img._id === imagemSelecionada._id);
        if (isAlreadyLinked) return setAlert('Esta imagem já está na oferta.', 'info');

        const novosIds = [...(oferta.imagensDaGaleria || []).map(img => img._id || img), imagemSelecionada._id];
        
        updateOferta.mutate({ id: oferta._id, ofertaData: { imagensDaGaleria: novosIds } }, {
            onSuccess: (response) => {
                setShowGalleryModal(false);
                onUpdate(response.data.data);
            }
        });
    };

    const handleGalleryImageRemove = (imagemId) => {
        if (window.confirm('Tem certeza que deseja desvincular esta imagem da galeria?')) {
            const novosIds = (oferta.imagensDaGaleria || []).map(img => img._id || img).filter(id => id !== imagemId);
            let payload = { imagensDaGaleria: novosIds };
            
            if (oferta.imagemPrincipal?.refId === imagemId && oferta.imagemPrincipal?.tipo === 'galeria') {
                payload.imagemPrincipal = null;
            }
            
            updateOferta.mutate({ id: oferta._id, ofertaData: payload }, {
                 onSuccess: (response) => {
                    onUpdate(response.data.data);
                 }
            });
        }
    };

    const handleSetMainImage = (tipo, refId) => {
        if (oferta.imagemPrincipal?.refId === refId) return;
        
        setOfertaImagemPrincipal.mutate({ ofertaId: oferta._id, tipo, refId }, {
             onSuccess: (response) => {
                onUpdate(response.data.data);
             }
        });
    };

    const isPrincipal = (img) => {
        return oferta.imagemPrincipal?.refId?.toString() === img._id?.toString();
    };

    if (isCreating) {
        return (
            <fieldset className="form-section">
                <legend className="form-section-title">Imagens da Oferta</legend>
                <div className="panel-state-wrapper panel-info">
                    <Info size={24} className="icon"/>
                    <span>
                        Primeiro, salve a oferta nas outras abas. Após salvar, as opções de gerenciamento de imagens aparecerão aqui.
                    </span>
                </div>
            </fieldset>
        );
    }

    return (
        <fieldset className="form-section" disabled={loading}>
            <legend className="form-section-title">Imagens da Oferta</legend>
            
            <div className="indented-panel">
                <h4 className="panel-subtitle">Buscar na Galeria Global</h4>
                <button type="button" onClick={() => setShowGalleryModal(true)} className="action-button action-button-secondary mt-2">
                    <SearchIcon size={16} className="icon" /> Buscar na Galeria
                </button>
            </div>
            
            <div className="indented-panel mt-4">
                <h4 className="panel-subtitle">Adicionar Nova Imagem Customizada</h4>
                <div className="flex items-center gap-4 flex-wrap mt-2">
                    <div className="w-24 h-24 border-2 border-dashed rounded-lg flex items-center justify-center bg-gray-50">
                        {imagePreview ? <img src={imagePreview} alt="Preview" className="w-full h-full object-cover rounded-md" /> : <ImageIcon size={32} className="text-gray-400" />}
                    </div>
                    <div className="form-group flex-1 min-w-[250px]">
                        <label htmlFor="oferta-image-upload-component">Escolher arquivo</label>
                        <input type="file" id="oferta-image-upload-component" onChange={handleFileChange} accept="image/*" className="form-input" />
                    </div>
                    <button type="button" onClick={handleImageUpload} disabled={!imageFile || loading} className="action-button action-button-primary">
                        {uploadOfertaImagem.isPending ? <Loader2 className="animate-spin icon"/> : <UploadCloud size={18} className="icon" />}
                        Enviar Imagem
                    </button>
                </div>

                <GerarImagemIABox
                    aspectRatio="3:2"
                    aspectHint="Tamanho ideal: 800×600px, paisagem. Evite fotos na vertical."
                    placeholder='Ex: "banner de oferta promocional, cores vibrantes, sem texto"'
                    onUseImage={(file, previewUrl) => { setImageFile(file); setImagePreview(previewUrl); }}
                    itemContexto={{ tipo: 'oferta', nome: oferta?.nome, descricao: oferta?.descricao, categoria: oferta?.categoria?.nome }}
                />
            </div>
            
            <div className="mt-6">
                <h4 className="panel-subtitle">Galeria de Imagens da Oferta</h4>
                {todasImagens.length > 0 ? (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 mt-4">
                        {todasImagens.map(img => (
                            <div key={img._id} className="relative group border rounded-lg overflow-hidden shadow-sm aspect-square">
                                <img src={img.url || img.arquivo} alt={img.nome || 'Imagem da oferta'} className="w-full h-full object-cover" />
                                {isPrincipal(img) && (
                                    <div className="absolute top-1 right-1 bg-amber-400 text-white p-1 rounded-full z-10">
                                        <Star size={14} fill="white" />
                                    </div>
                                )}
                                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all flex items-center justify-center gap-2">
                                    <button 
                                        type="button"
                                        onClick={() => handleSetMainImage(img.tipo, img._id)}
                                        disabled={isPrincipal(img) || loading}
                                        title="Definir como principal"
                                        className="p-2 bg-white rounded-full text-gray-700 opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed">
                                        <Star size={18} />
                                    </button>
                                    
                                    {img.tipo === 'galeria' ? (
                                        <button 
                                            type="button"
                                            onClick={() => handleGalleryImageRemove(img._id)}
                                            disabled={loading}
                                            title="Desvincular Imagem"
                                            className="p-2 bg-yellow-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
                                            <XCircle size={18} />
                                        </button>
                                    ) : (
                                        <button 
                                            type="button"
                                            onClick={() => handleCustomImageDelete(img._id)}
                                            disabled={loading}
                                            title="Remover Imagem"
                                            className="p-2 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
                                            <Trash2 size={18} />
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="panel-state-wrapper panel-info mt-4">
                        <ImageIcon size={24} className="icon"/>
                        <span>Esta oferta ainda não possui imagens.</span>
                    </div>
                )}
            </div>
            
            {showGalleryModal && (
                <GaleriaBuscaModal 
                    onClose={() => setShowGalleryModal(false)}
                    onImageSelect={handleGalleryImageSelect}
                    imagensJaVinculadas={(oferta.imagensDaGaleria || []).map(img => img._id)}
                />
            )}
        </fieldset>
    );
};

OfertaImages.propTypes = {
    oferta: PropTypes.object.isRequired,
    onUpdate: PropTypes.func.isRequired,
    isCreating: PropTypes.bool 
};

export default OfertaImages;